import { getWidgetSelectors } from "./getWidgetSelectors"

export type ElementData = {
  integrationId: string | null
  stockId: string | null
  customData?: Record<string, string | number> | string
  carUrl: string | null
  departmentId: string | null
  hasExternalLogo: boolean | null
  sellerUrl?: string
}

export type ParsedWidget = {
  elTarget: HTMLElement
  selector: string
  leadFlowId?: number
  elData: ElementData
}

const parseCustomData = (
  json: string | null,
  selector: string
): ElementData["customData"] => {
  if (!json) return
  try {
    return JSON.parse(json)
  } catch (e) {
    console.error(`Error parsing custom data for ${selector}: ${json}`, e)
    return json
  }
}

export const parseElementsForSelector = (
  selector: string,
  data?: { leadFlowId: number }
): ParsedWidget[] => {
  const elems = document.querySelectorAll<HTMLElement>(selector)
  const parsedData: ParsedWidget[] = []

  for (let i = 0, len = elems.length; i < len; i++) {
    const e = elems[i]

    if (!e) {
      continue
    }

    const customDataJson = e.getAttribute("data-custom-data")
    const customData = parseCustomData(customDataJson, selector)

    parsedData.push({
      elTarget: e,
      selector: selector,
      leadFlowId: data?.leadFlowId,
      elData: {
        integrationId: e.getAttribute("data-integration-id"),
        stockId: e.getAttribute("data-stock-id"),
        customData,
        carUrl: e.getAttribute("data-car-url"),
        departmentId: e.getAttribute("data-department-id"),
        hasExternalLogo:
          e.nextElementSibling &&
          /au-trade-in-backlink/.test(e.nextElementSibling.className),
      },
    })
  }

  return parsedData
}

/* Price Rating Widget */
export const parsePriceRatingWidgetElementsForSelector = (
  selector: string,
  options?: { carUrlSelector: string; carItemSelector: string }
): ParsedWidget[] => {
  const elems = document.querySelectorAll<HTMLElement>(selector)
  const parsedData: ParsedWidget[] = []

  for (let i = 0, len = elems.length; i < len; i++) {
    const e = elems[i]

    if (!e) {
      continue
    }

    const customData = parseCustomData(
      e.getAttribute("data-custom-data"),
      selector
    )
    let carUrl = e.getAttribute("data-car-url")

    if (carUrl === null && options !== undefined) {
      const carItemElement = e.closest<HTMLElement>(options.carItemSelector)
      if (carItemElement) {
        const carUrlElement = carItemElement.matches(options.carUrlSelector)
          ? (carItemElement as HTMLAnchorElement)
          : carItemElement.querySelector<HTMLAnchorElement>(
              options.carUrlSelector
            )

        if (carUrlElement) {
          carUrl = carUrlElement.href
        }
      }
    }

    parsedData.push({
      elTarget: e,
      selector: selector,
      elData: {
        integrationId: e.getAttribute("data-integration-id"),
        stockId: e.getAttribute("data-stock-id"),
        customData,
        carUrl,
        departmentId: e.getAttribute("data-department-id"),
        // for Trade-In, does not apply
        hasExternalLogo: null,
      },
    })
  }

  return parsedData
}

/**
 * Returns an array of widgets that should be shown on the dealer's website
 * based on found elements with specific class names
 */
export const parseWidgets = (
  customWidgetSelectors?: Record<string, string | undefined>
): ParsedWidget[] => {
  const widgetSelectors = getWidgetSelectors(customWidgetSelectors)

  return Object.keys(widgetSelectors).flatMap((widget) =>
    parseElementsForSelector(
      widgetSelectors[widget as keyof typeof widgetSelectors]
    )
  )
}

export const getCustomerCarUrls = (widgets: ParsedWidget[]) => {
  return widgets
    .map((widget) => widget.elData.carUrl)
    .filter((url) => url !== null)
}
